import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { tenantNavLinkKeys, type TenantNavLinkKey } from "@lms/backend/lib/nav/tenant-nav-links";
import { ThemeToggle } from "@/components/theme-toggle";
import { LocaleSwitcher } from "@/components/locale-switcher";
import { Menu, MenuItem, menuItemClasses } from "@/components/ui/menu";
import { readApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { logoutSession } from "@/services/api/auth";
import { useAuthStore } from "@/stores/authStore";

type TenantNavProps = {
  tenantSlug?: string;
  isAuthenticated?: boolean;
  userRole?: string;
  tenantName?: string;
  logoUrl?: string | null;
};

const PRIMARY_KEYS: TenantNavLinkKey[] = [
  "courses",
  "about",
  "dashboard",
  "instructor",
  "instructorCourses",
  "forum",
];

function hrefForNavKey(slug: string, key: TenantNavLinkKey): string {
  switch (key) {
    case "courses":
      return `/t/${slug}/courses`;
    case "about":
      return `/t/${slug}/about`;
    case "forum":
      return `/t/${slug}/forum`;
    case "messages":
      return `/t/${slug}/messages`;
    case "notifications":
      return `/t/${slug}/notifications`;
    case "admin":
      return `/t/${slug}/admin/members`;
    case "branding":
      return `/t/${slug}/admin/branding`;
    case "email":
      return `/t/${slug}/admin/email`;
    case "approvedEmails":
      return `/t/${slug}/admin/approved-emails`;
    case "joinRequests":
      return `/t/${slug}/admin/join-requests`;
    case "accessReport":
      return `/t/${slug}/admin/access-report`;
    case "dashboard":
      return `/t/${slug}/dashboard`;
    case "instructor":
      return `/t/${slug}/instructor`;
    case "instructorCourses":
      return `/t/${slug}/instructor/courses`;
  }
}

/**
 * The account control sits next to the mobile nav drawer toggle, and both were
 * hamburgers — same shape, different meaning, neither guessable. The drawer
 * keeps the hamburger; the account menu gets a person glyph so the pair is
 * distinguishable at a glance rather than only to a screen reader.
 */
function AccountGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 19.5a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

export function TenantNav({
  tenantSlug: tenantSlugProp,
  isAuthenticated = false,
  userRole,
  tenantName,
  logoUrl,
}: TenantNavProps) {
  const params = useParams<{ tenantSlug: string }>();
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const slug = tenantSlugProp ?? params.tenantSlug ?? "";
  const t = useTranslations("nav");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const clearSession = useAuthStore((s) => s.clearSession);
  // MT-1 FR-8.7: a logo URL that fails to load falls back to the school's name as text. Keyed by
  // URL, so a new `logoUrl` is tried again without an effect.
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const showLogo = Boolean(logoUrl) && logoUrl !== failedLogoUrl;

  const accent = "var(--accent)";

  const allKeys = tenantNavLinkKeys(userRole, isAuthenticated);
  const toLink = (key: TenantNavLinkKey) => ({
    key,
    href: hrefForNavKey(slug, key),
    label: t(key),
  });
  const primary = allKeys.filter((key) => PRIMARY_KEYS.includes(key)).map(toLink);
  const secondary = allKeys.filter((key) => !PRIMARY_KEYS.includes(key)).map(toLink);

  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

  function navLinkClass(href: string) {
    return cn(
      "rounded-full px-3 py-1.5 text-sm transition",
      isActive(href)
        ? "bg-white/15 font-semibold text-white"
        : "text-white/80 hover:bg-white/10 hover:text-white",
    );
  }

  async function handleSignOut() {
    // S3 V-17: the server refuses a cookie-only logout from an origin it does not trust; this
    // tab is signed out anyway, and the login page says the server sign-out did not happen.
    let query = "";
    try {
      await logoutSession();
    } catch (err) {
      if (readApiError(err)?.code === "ORIGIN_NOT_ALLOWED") query = "?reason=signout_failed";
    } finally {
      clearSession();
      navigate(`/t/${slug}/login${query}`);
    }
  }

  return (
    <header
      className="sticky top-0 z-40 border-b border-black/20 text-white shadow-md"
      style={{ backgroundColor: "var(--brand-purple)" }}
      data-testid="tenant-nav"
      data-nav-role={userRole ?? "guest"}
    >
      <div className="h-1 w-full" style={{ backgroundColor: accent }} aria-hidden />
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-6">
        <Link
          to={`/t/${slug}/courses`}
          className="flex min-w-0 shrink-0 items-center gap-3 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          {showLogo ? (
            <img
              src={logoUrl ?? undefined}
              // ACC: the school's name is the link's text (sr-only below); the logo is decorative.
              alt=""
              className="h-8 w-auto max-w-[128px] object-contain sm:h-10 sm:max-w-[170px]"
              data-testid="tenant-logo"
              onError={() => setFailedLogoUrl(logoUrl ?? null)}
            />
          ) : (
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--accent-strong)] text-sm font-bold text-[var(--accent-foreground)]"
              aria-hidden
            >
              {(tenantName ?? slug).slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className={cn("min-w-0", showLogo && "sr-only")} data-testid="tenant-name">
            <span className="block truncate font-semibold tracking-wide">{tenantName ?? slug}</span>
          </span>
        </Link>

        <nav aria-label={t("primary")} className="hidden flex-1 items-center gap-1 lg:flex">
          {primary.map((link) => (
            <Link
              key={link.key}
              to={link.href}
              data-testid={`nav-${link.key}`}
              aria-current={isActive(link.href) ? "page" : undefined}
              className={navLinkClass(link.href)}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <LocaleSwitcher />
          <ThemeToggle />

          {isAuthenticated ? (
            <Menu
              label={t("account")}
              data-testid="nav-account-menu"
              triggerClassName="flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-full border border-white/30 px-3 text-xs font-semibold text-white transition hover:bg-white/10"
              trigger={<AccountGlyph />}
            >
              {secondary.map((link) => (
                <MenuItem key={link.key}>
                  <Link to={link.href} data-testid={`nav-${link.key}`} className={menuItemClasses}>
                    {link.label}
                  </Link>
                </MenuItem>
              ))}
              <MenuItem>
                <button
                  type="button"
                  onClick={() => void handleSignOut()}
                  data-testid="nav-sign-out"
                  className={menuItemClasses}
                >
                  {t("signOut")}
                </button>
              </MenuItem>
            </Menu>
          ) : (
            <Link
              to={`/t/${slug}/login`}
              data-testid="nav-sign-in"
              // ACC: the contrast-solved accent pair (deriveAccentTokens), never the school's raw
              // colour under fixed ink — any brand colour keeps the label at >=4.5:1.
              className="rounded-full bg-[var(--accent-strong)] px-4 py-1.5 text-xs font-semibold text-[var(--accent-foreground)] transition hover:bg-[var(--accent-strong-hover)]"
            >
              {t("signIn")}
            </Link>
          )}

          {primary.length > 0 && (
            <button
              type="button"
              className="flex min-h-11 min-w-11 items-center justify-center rounded-full border border-white/30 px-3 py-1.5 text-xs text-white lg:hidden"
              aria-expanded={drawerOpen}
              aria-controls="tenant-nav-drawer"
              aria-label={t("primary")}
              data-testid="nav-mobile-toggle"
              onClick={() => setDrawerOpen((value) => !value)}
            >
              <span aria-hidden>≡</span>
            </button>
          )}
        </div>
      </div>

      {drawerOpen && (
        <nav
          id="tenant-nav-drawer"
          aria-label={t("primary")}
          className="border-t border-white/10 px-4 pb-3 lg:hidden"
        >
          <ul className="flex flex-col gap-1 pt-2">
            {primary.map((link) => (
              <li key={link.key}>
                <Link
                  to={link.href}
                  aria-current={isActive(link.href) ? "page" : undefined}
                  className={cn(navLinkClass(link.href), "block min-h-11 leading-8")}
                  onClick={() => setDrawerOpen(false)}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
}
